<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import KBtn from '@/components/btns/KBtn.vue';
import { useDialogStore } from '@/stores/useDialogStore';
import * as commonConstants from '@/constants/common';

export interface Props {
  image?: ReadonlyDeep<openapi.GetStopImagesContentOpenapi[0][0]>;
  deleteImage?: () => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  image: undefined,
  deleteImage: undefined,
});

const dialogStore = useDialogStore();

function openDialog() {
  const dialogName: DialogName = 'detailedJobPhoto';
  dialogStore.open(dialogName, { image: props.image });
}

function openDeleteDialog() {
  const dialogName: DialogName = 'deleteJobPhoto';
  dialogStore
    .open(dialogName, { image: props.image })
    .confirmed(async function () {
      if (props.deleteImage) {
        await props.deleteImage();
      }
    });
}
</script>

<template>
  <div
    v-if="props.image"
    :class="$style.detailedJobPhotosDescListItem"
    data-testid="detailedJobPhotosDescListItem"
  >
    <dt :class="$style.dt">
      {{ props.image.ref || commonConstants.EM_DASH }}
    </dt>
    <dd :class="$style.dd">
      <template v-if="props.image.path">
        <v-img
          :class="$style.img"
          :src="props.image.path"
          width="160"
          height="180"
          cover
          @click="openDialog()"
        >
          <template #placeholder>
            <v-skeleton-loader
              :class="$style.skeleton"
              type="image"
              width="160"
              height="180"
            />
          </template>
        </v-img>
        <KBtn
          :class="$style.downloadImgBtn"
          size="sm"
          priority="info"
          icon="mdi-download-outline"
          rounded="circle"
          target="_blank"
          :href="props.image.path"
        />
        <KBtn
          :class="$style.deleteImgBtn"
          size="sm"
          priority="danger"
          icon="mdi-delete-outline"
          rounded="circle"
          @click="openDeleteDialog()"
        />
      </template>
    </dd>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobPhotosDescListItem {
  display: inline-flex;
  flex-direction: column;
}
  .dt {
    order: 2;
    margin-top: 5px;
  }
  .dd {
    order: 1;
    margin: 0;
    position: relative;
    width: 160px;
    height: 180px;
  }
    .img {
      cursor: pointer;
    }
    .downloadImgBtn {
      font-size: 16px !important;
      position: absolute;
      bottom: 8px;
      right: 8px;
    }
    .deleteImgBtn {
      font-size: 16px !important;
      position: absolute;
      top: 8px;
      right: 8px;
      background-color: colors.$color-red-500 !important;
    }
    .skeleton :global(.v-skeleton-loader__image) {
      height: 180px;
    }
</style>
